import React, { useState, useRef, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { connect, useDispatch } from "react-redux";
import toast from '../shared/Toast';
import { compressImages, isHeicFile } from '../shared/imageCompress';
import { productAction } from "../../_actions/product.action";
import { productService } from "../../_services/product.service";
import InlinePickerSelect from "../shared/InlinePickerSelect";
import CatalogueTopNav from "../new-service/CatalogueTopNav";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import "../new-service/newservice.css";
import "../new-service/ServiceEditorPanel.css";
import "./product.css";

function EditProduct(props) {
  // Product Details
  const [productName, setProductName] = useState("");
  const [productCategory, setProductCategory] = useState(null);
  const [productBrand, setProductBrand] = useState(null);
  const [description, setDescription] = useState("");
  
  // Product Images
  const [productImages, setProductImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [deletedImages, setDeletedImages] = useState([]);
  const fileInputRef = useRef(null);
  
  // Pricing
  const [retailPrice, setRetailPrice] = useState("");
  const [stockPrice, setStockPrice] = useState("");
  const [commissionEnable, setCommissionEnable] = useState("no");
  
  // Stock
  const [barcode, setBarcode] = useState("");
  const [sku, setSku] = useState("");
  const [trackStock, setTrackStock] = useState("yes");
  const [sellOnline, setSellOnline] = useState("no");
  
  const [currencySymbol, setCurrencySymbol] = useState("");
  const [errors, setErrors] = useState({});
  
  // Initialize currency symbol from user profile
  useEffect(() => {
    if (props?.user?.userInfo?.hairdresser?.currencySymbol) {
      setCurrencySymbol(props.user.userInfo.hairdresser.currencySymbol);
    }
  }, [props?.user?.userInfo?.hairdresser?.currencySymbol]);
  const [activeTab, setActiveTab] = useState("details");
  const [productId, setProductId] = useState("");
  
  const dispatch = useDispatch();
  const history = useHistory();

  // State for category and brand options
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [brandOptions, setBrandOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Fetch categories and brands on component mount
  useEffect(() => {
    setIsLoading(true);
    
    // Fetch categories
    dispatch(productAction.listProductCategories())
      .then((response) => {
        console.log("Categories response:", response);
        const categories = response?.data?.responseData?.categories || [];
        const formattedCategories = categories.map(category => ({
          value: category.categoryId,
          label: category.categoryName
        }));
        setCategoryOptions(formattedCategories);
      })
      .catch((err) => {
        console.error("Error fetching categories:", err);
        toast.error("Failed to load product categories");
      });
    
    // Fetch brands
    dispatch(productAction.listProductBrands())
      .then((response) => {
        console.log("Brands response:", response);
        const brands = response?.data?.responseData?.brands || [];
        const formattedBrands = brands.map(brand => ({
          value: brand.brandId,
          label: brand.brandName
        }));
        setBrandOptions(formattedBrands);
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching brands:", err);
        toast.error("Failed to load product brands");
        setIsLoading(false);
      });
  }, [dispatch]);
  
  // Load product data on component mount
  useEffect(() => {
    const { location } = props;
    if (location.state && location.state.product) {
      const { product } = location.state;
      console.log("Loading product data:", product);
      
      // Set basic product details
      // Check for both _id and id since the product might have either
      const productId = product._id || product.id;
      
      if (!productId) {
        console.error("Product ID is missing in both product._id and product.id!");
        console.log("Full product object:", JSON.stringify(product));
      }
      
      setProductId(productId);
      setProductName(product.productName || "");
      setDescription(product.description || "");
      setRetailPrice(product.retailPrice || "");
      setStockPrice(product.stockPrice || "");
      setCommissionEnable(product.commissionEnable === 1 ? "yes" : "no");
      setBarcode(product.barcode || "");
      setSku(product.sku || "");
      setTrackStock(product.trackStock === 1 ? "yes" : "no");
      setSellOnline(product.sellOnline === 1 ? "yes" : "no");
      
      // Set existing images if available
      if (product.images && product.images.length > 0) {
        console.log("Setting existing images:", product.images);
        setExistingImages(product.images.map((img, index) => ({
          id: img._id || `existing-${index}`,
          url: img.url,
          key: img.key,
          order: index // Add order property to track original order
        })));
      }
      
      // Log the product ID for debugging
      console.log("Product ID set to:", productId);
    } else {
      console.warn("No product data found in location state");
    }
  }, [props.location]);
  
  // Set category and brand when options are loaded and product data is available
  useEffect(() => {
    const { location } = props;
    if (location.state && location.state.product && categoryOptions.length > 0 && brandOptions.length > 0) {
      const { product } = location.state;
      
      // Set category if available
      if (product.categoryId) {
        const category = categoryOptions.find(cat => cat.value === product.categoryId);
        if (category) {
          setProductCategory(category);
        } else if (product.category) {
          // If category object is available from the API
          setProductCategory({
            value: product.category._id,
            label: product.category.categoryName
          });
        }
      }
      
      // Set brand if available
      if (product.brandId) {
        const brand = brandOptions.find(b => b.value === product.brandId);
        if (brand) {
          setProductBrand(brand);
        } else if (product.brand) {
          // If brand object is available from the API
          setProductBrand({
            value: product.brand._id,
            label: product.brand.brandName
          });
        }
      }
    }
  }, [props.location, categoryOptions, brandOptions]);

  // Validation function
  const validateFields = (name, value) => {
    let error = '';
    switch (name) {
      case 'productName':
        if (!value) {
          error = "Product Name is required.";
        }
        break;
      case 'productCategory':
        if (!value) {
          error = "Product Category is required.";
        }
        break;
      case 'retailPrice':
        if (!value) {
          error = "Retail Price is required.";
        } else if (isNaN(value)) {
          error = "Retail Price must be a number.";
        }
        break;
      case 'stockPrice':
        if (!value) {
          error = "Stock Price is required.";
        } else if (isNaN(value)) {
          error = "Stock Price must be a number.";
        } else if (parseFloat(stockPrice) > parseFloat(retailPrice)) {
          error = "Stock Price cannot be greater than Retail Price.";
        }
        break;
      default:
        break;
    }
    return error;
  };

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;

    switch (name) {
      case 'productName':
        setProductName(value);
        break;
      case 'retailPrice':
        if (!isNaN(value)) {
          setRetailPrice(value);
        }
        break;
      case 'stockPrice':
        if (!isNaN(value)) {
          setStockPrice(value);
        }
        break;
      case 'description':
        setDescription(value);
        break;
      case 'barcode':
        setBarcode(value);
        break;
      case 'sku':
        setSku(value);
        break;
      default:
        break;
    }

    setErrors(prev => ({
      ...prev,
      [name]: validateFields(name, value)
    }));
  };

  // Handle dropdown changes
  const handleCategoryChange = (selected) => {
    setProductCategory(selected);
    setErrors(prev => ({
      ...prev,
      productCategory: validateFields('productCategory', selected)
    }));
  };

  const handleBrandChange = (selected) => {
    setProductBrand(selected);
  };

  // Handle image upload
  const handleImageUpload = async (e) => {
   try {
    const files = Array.from(e.target.files);

    // Check file type and size (large source photos are allowed — they're
    // optimised below before upload).
    const validFiles = files.filter(file => {
      const isValidType = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'].includes(file.type) || isHeicFile(file);
      const isValidSize = file.size <= 25 * 1024 * 1024;

      if (!isValidType) {
        toast.error(`${file.name} is not a supported image format.`);
      }

      if (!isValidSize) {
        toast.error(`${file.name} is too large (max 25MB).`);
      }

      return isValidType && isValidSize;
    });

    if (validFiles.length === 0) return;

    // Downscale/re-encode big images so they fit under the upload size limit.
    let processed = validFiles;
    try { processed = await compressImages(validFiles); } catch (err) { processed = validFiles; }
    // A HEIC that survived compression means this browser can't decode iPhone
    // photos (WebKit can; Chrome/Windows can't). Say so instead of uploading
    // a file the storefront can never display.
    processed = processed.filter((f) => {
      if (isHeicFile(f)) {
        toast.error(`${f.name} is an iPhone photo in HEIC format, which browsers can't read. Pick it from your Photo Library instead (it converts automatically), or use a JPG/PNG copy.`);
        return false;
      }
      return true;
    });
    if (processed.length === 0) return;

    // Create preview URLs for valid files
    const newImages = processed.map(file => ({
      id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      file,
      preview: URL.createObjectURL(file),
      order: existingImages.length + productImages.length // Set order after existing images and current new images
    }));

    setProductImages(prev => [...prev, ...newImages]);
   } catch (err) {
    // iOS Safari can invalidate picked files mid-processing; say so instead
    // of doing nothing.
    console.error('Image add failed', err);
    toast.error('Could not read that photo. Please try again.');
   }
  };

  // Handle image removal
  const handleRemoveImage = (index) => {
    setProductImages(prev => {
      const newImages = [...prev];
      // Revoke the object URL to avoid memory leaks
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  // Handle existing image removal
  const handleRemoveExistingImage = (index) => {
    const imageToRemove = existingImages[index];
    setDeletedImages(prev => [...prev, imageToRemove]);
    
    setExistingImages(prev => {
      const newImages = [...prev];
      newImages.splice(index, 1);
      return newImages;
    });
  };

  // Handle barcode scanner
  const handleScanBarcode = () => {
    // This would be implemented with a barcode scanning library
    toast.info("Barcode scanner functionality would be implemented here");
  };

  // Handle save button click
  const handleSave = () => {
    setErrors({});
    
    // Check if productId is available
    if (!productId) {
      console.error("Product ID is missing!");
      
      // Try to get the product ID from the location state as a fallback
      const { location } = props;
      if (location.state && location.state.product) {
        const { product } = location.state;
        const fallbackId = product._id || product.id || location.state.productId;
        
        if (fallbackId) {
          console.log("Found fallback product ID:", fallbackId);
          setProductId(fallbackId);
        } else {
          toast.error("Cannot update product: Product ID is missing");
          return;
        }
      } else {
        toast.error("Cannot update product: Product ID is missing");
        return;
      }
    }
    
    console.log("Saving product with ID:", productId);
    
    const newErrors = {
      productName: validateFields('productName', productName),
      productCategory: validateFields('productCategory', productCategory),
      retailPrice: validateFields('retailPrice', retailPrice),
      stockPrice: validateFields('stockPrice', stockPrice)
    };
    
    const filteredErrors = Object.fromEntries(Object.entries(newErrors).filter(([_, v]) => v));
    setErrors(filteredErrors);

    if (Object.keys(filteredErrors).length > 0) {
      // Show which tab has errors
      if (filteredErrors.productName || filteredErrors.productCategory) {
        setActiveTab("details");
      } else if (filteredErrors.retailPrice || filteredErrors.stockPrice) {
        setActiveTab("pricing");
      }
      return;
    }

    if (parseFloat(stockPrice) > parseFloat(retailPrice)) {
      toast.error("Stock price cannot be greater than retail price.");
      setActiveTab("pricing");
      return;
    }
  
    // Create FormData for image upload
    const formData = new FormData();
    
    // Ensure productId is included and is a string
    formData.append("productId", String(productId));
    
    formData.append("productName", productName);
    formData.append("retailPrice", parseFloat(retailPrice));
    formData.append("stockPrice", parseFloat(stockPrice));
    formData.append("commissionEnable", commissionEnable === "no" ? 0 : 1);
    formData.append("description", description);
    
    if (productCategory) {
      formData.append("categoryId", productCategory.value);
    }
    
    if (productBrand) {
      formData.append("brandId", productBrand.value);
    }
    
    formData.append("barcode", barcode);
    formData.append("sku", sku);
    formData.append("trackStock", trackStock === "yes" ? 1 : 0);
    formData.append("sellOnline", sellOnline === "yes" ? 1 : 0);
    
    // Append existing images that weren't deleted with order information
    formData.append("existingImages", JSON.stringify(existingImages.map((img, index) => ({
      ...img,
      order: index
    }))));
    
    // Append deleted images
    formData.append("deletedImages", JSON.stringify(deletedImages));
    
    // Create a combined image order array with both existing and new images
    const allImagesOrder = [
      ...existingImages.map((img, index) => ({
        id: img.id,
        isExisting: true,
        order: index
      })),
      ...productImages.map((img, index) => ({
        id: img.id,
        isExisting: false,
        order: existingImages.length + index
      }))
    ];
    
    // Append the combined image order information
    formData.append("imageOrder", JSON.stringify(allImagesOrder));
    
    // Append the actual image files
    productImages.forEach((image) => {
      formData.append('images', image.file);
    });
    
    // Log the form data for debugging
    console.log("Form data productId:", formData.get("productId"));
    console.log("Image order:", JSON.parse(formData.get("imageOrder")));
    
    // For now, we'll use the existing API but in a real implementation
    // we would update the backend to handle the new fields
    dispatch(productAction.updateProduct(formData))
      .then((response) => {
        console.log('Update response', response);
        toast.success("Product Updated Successfully");
        history.push("/product");
      })
      .catch((err) => {
        console.log("Error updating product:", err);
        toast.error("Failed to update product: " + (err.message || "Unknown error"));
      });
  };

  // Render product details section
  const renderProductDetails = () => (
    <div className="sep-section">
      <p className="sep-section-label">Product Details</p>

      <div className="sep-field">
        <label className="sep-field-label">Product Name</label>
        <input
          type="text"
          name="productName"
          placeholder="e.g. Shampoo"
          className={`sep-input ${errors.productName ? 'sep-input--error' : ''}`}
          value={productName}
          onChange={handleChange}
        />
        {errors.productName && <span className="sep-error">{errors.productName}</span>}
      </div>

      <div className="sep-field">
        <label className="sep-field-label">Category</label>
        <InlinePickerSelect
          options={categoryOptions}
          value={productCategory}
          onChange={handleCategoryChange}
          onCreateNew={async (name) => {
            try {
              const response = await productService.addProductCategory({ categoryName: name });
              const rd = response?.data?.responseData || response?.data?.data || response?.data || {};
              const newCat = rd.category || rd.result || rd;
              const catId = newCat.categoryId || newCat._id || newCat.id;
              if (catId) {
                const option = { value: catId, label: name };
                setCategoryOptions(prev => [...prev, option]);
                handleCategoryChange(option);
                toast.success('Category created');
              } else {
                const res = await dispatch(productAction.listProductCategories());
                const cats = res?.data?.responseData?.categories || [];
                const formatted = cats.map(c => ({ value: c.categoryId, label: c.categoryName }));
                setCategoryOptions(formatted);
                const match = formatted.find(c => c.label === name);
                if (match) handleCategoryChange(match);
                toast.success('Category created');
              }
            } catch { toast.error('Failed to create category'); throw new Error(); }
          }}
          placeholder="Choose a Category"
          createLabel="Create new category"
          searchPlaceholder="Search categories..."
          error={errors.productCategory}
        />
      </div>

      <div className="sep-field">
        <label className="sep-field-label">Brand</label>
        <InlinePickerSelect
          options={brandOptions}
          value={productBrand}
          onChange={handleBrandChange}
          onCreateNew={async (name) => {
            try {
              const response = await productService.addProductBrand({ brandName: name });
              const rd = response?.data?.responseData || response?.data?.data || response?.data || {};
              const newBr = rd.brand || rd.result || rd;
              const brId = newBr.brandId || newBr._id || newBr.id;
              if (brId) {
                const option = { value: brId, label: name };
                setBrandOptions(prev => [...prev, option]);
                handleBrandChange(option);
                toast.success('Brand created');
              } else {
                const res = await dispatch(productAction.listProductBrands());
                const brands = res?.data?.responseData?.brands || [];
                const formatted = brands.map(b => ({ value: b.brandId || b._id, label: b.brandName }));
                setBrandOptions(formatted);
                const match = formatted.find(b => b.label === name);
                if (match) handleBrandChange(match);
                toast.success('Brand created');
              }
            } catch { toast.error('Failed to create brand'); throw new Error(); }
          }}
          placeholder="Choose a Brand"
          createLabel="Create new brand"
          searchPlaceholder="Search brands..."
        />
      </div>

      <div className="sep-field">
        <label className="sep-field-label">Description</label>
        <textarea
          className="sep-input sep-textarea"
          name="description"
          placeholder="Add a description to give prospective clients more information about your product."
          value={description}
          onChange={handleChange}
        />
      </div>
    </div>
  );


  // Setup DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // These handlers are no longer needed as we're using a unified drag and drop
  // They're replaced by handleAllImagesDragEnd

  // Combine existing and new images for unified drag and drop
  const allImages = [...existingImages.map(img => ({
    ...img,
    isExisting: true
  })), ...productImages.map(img => ({
    ...img,
    isExisting: false
  }))];

  // Handle drag end for all images
  const handleAllImagesDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      // Find the indices in the combined array
      const oldIndex = allImages.findIndex(item => item.id === active.id);
      const newIndex = allImages.findIndex(item => item.id === over.id);
      
      // Create a new array with the reordered items
      const newAllImages = arrayMove([...allImages], oldIndex, newIndex);
      
      // Split back into existing and new images
      const newExistingImages = newAllImages
        .filter(img => img.isExisting)
        .map(img => ({
          ...img,
          isExisting: undefined // Remove the temporary property
        }));
      
      const newProductImages = newAllImages
        .filter(img => !img.isExisting)
        .map(img => ({
          ...img,
          isExisting: undefined // Remove the temporary property
        }));
      
      // Update state
      setExistingImages(newExistingImages);
      setProductImages(newProductImages);
    }
  };

  // Sortable Image Item — sep-image-item style, drag-to-reorder
  const SortableImageItem = ({ image, index }) => {
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
    } = useSortable({ id: image.id });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition,
    };

    const isExisting = image.isExisting;

    return (
      <div
        ref={setNodeRef}
        style={{ ...style, cursor: 'grab' }}
        className="sep-image-item"
        {...attributes}
        {...listeners}
      >
        <img src={isExisting ? image.url : image.preview} alt={`Product ${index}`} />
        <button
          type="button"
          className="sep-image-delete"
          onClick={(e) => {
            e.stopPropagation();
            if (isExisting) {
              handleRemoveExistingImage(existingImages.findIndex(img => img.id === image.id));
            } else {
              handleRemoveImage(productImages.findIndex(img => img.id === image.id));
            }
          }}
        >×</button>
      </div>
    );
  };

  // Render product images section
  const renderProductImages = () => (
    <div className="sep-section">
      <p className="sep-section-label">Images</p>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleAllImagesDragEnd}
      >
        <SortableContext
          items={allImages.map(img => img.id)}
          strategy={horizontalListSortingStrategy}
        >
          <div className="sep-images-grid">
            {allImages.map((image, index) => (
              <SortableImageItem key={image.id} image={image} index={index} />
            ))}
            <div className="sep-image-upload" style={{ position: 'relative' }} onClick={() => fileInputRef.current && fileInputRef.current.click()}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              <span>Add</span>
              <input
                type="file"
                ref={fileInputRef}
                accept="image/jpeg, image/png, image/webp"
                multiple
                onClick={(e) => {
                                    // The invisible input covers its wrapper tile, whose own
                                    // onClick calls input.click() — without stopPropagation the
                                    // bubble re-clicks the input and WebKit (Safari/iOS) opens a
                                    // SECOND file dialog, wiping the first pick. Chrome suppresses
                                    // the re-entrant dialog, which is why it only broke on Safari.
                                    e.stopPropagation();
                                    e.target.value = '';
                                }}
                onChange={handleImageUpload}
                style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer' }}
              />
            </div>
          </div>
        </SortableContext>
      </DndContext>
      <p style={{ fontSize: '12px', color: '#8e8e93', marginTop: '8px' }}>
        JPG, PNG or WEBP. Large photos are optimised automatically. {allImages.length > 1 ? 'Drag to reorder.' : ''}
      </p>
    </div>
  );

  // Render pricing section
  const renderPricing = () => (
    <div className="sep-section">
      <p className="sep-section-label">Pricing</p>

      <div className="sep-field-row">
        <div className="sep-field">
          <label className="sep-field-label">Retail Price ({currencySymbol})</label>
          <input
            type="text"
            name="retailPrice"
            placeholder="0.00"
            className={`sep-input ${errors.retailPrice ? 'sep-input--error' : ''}`}
            value={retailPrice}
            onChange={handleChange}
          />
          <span className="sep-field-hint">The price the item is sold at</span>
          {errors.retailPrice && <span className="sep-error">{errors.retailPrice}</span>}
        </div>
        <div className="sep-field">
          <label className="sep-field-label">Stock Price ({currencySymbol})</label>
          <input
            type="text"
            name="stockPrice"
            placeholder="0.00"
            className={`sep-input ${errors.stockPrice ? 'sep-input--error' : ''}`}
            value={stockPrice}
            onChange={handleChange}
          />
          <span className="sep-field-hint">The cost for the item to the business</span>
          {errors.stockPrice && <span className="sep-error">{errors.stockPrice}</span>}
          {parseFloat(stockPrice) > parseFloat(retailPrice) && (
            <span className="sep-error">Stock price cannot be greater than retail price.</span>
          )}
        </div>
      </div>

      <div className="sep-toggle-row" style={{ borderBottom: 'none' }}>
        <div className="sep-toggle-info">
          <div className="sep-toggle-title">Commission enabled</div>
          <div className="sep-toggle-desc">Pay staff a commission when this product sells</div>
        </div>
        <button
          type="button"
          className={`sep-toggle ${commissionEnable === 'yes' ? 'sep-toggle--on' : ''}`}
          onClick={() => setCommissionEnable(commissionEnable === 'yes' ? 'no' : 'yes')}
        >
          <span className="sep-toggle-knob" />
        </button>
      </div>
    </div>
  );

  // Render stock section
  const renderStock = () => (
    <div className="sep-section">
      <p className="sep-section-label">Stock</p>

      <div className="sep-field-row">
        <div className="sep-field">
          <label className="sep-field-label">Barcode</label>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              type="text"
              name="barcode"
              placeholder="Enter barcode"
              className="sep-input"
              value={barcode}
              onChange={handleChange}
              style={{ flex: 1 }}
            />
            <button
              type="button"
              onClick={handleScanBarcode}
              style={{
                width: '44px', height: '44px', borderRadius: '10px',
                border: '1px solid #e0e0e0', background: '#fff', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}
              aria-label="Scan barcode"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="5" width="3" height="14" rx="1" stroke="#333" strokeWidth="2"/>
                <rect x="8" y="5" width="2" height="14" rx="1" stroke="#333" strokeWidth="2"/>
                <rect x="12" y="5" width="1" height="14" rx="0.5" stroke="#333" strokeWidth="1"/>
                <rect x="15" y="5" width="1" height="14" rx="0.5" stroke="#333" strokeWidth="1"/>
                <rect x="18" y="5" width="3" height="14" rx="1" stroke="#333" strokeWidth="2"/>
              </svg>
            </button>
          </div>
        </div>
        <div className="sep-field">
          <label className="sep-field-label">SKU</label>
          <input
            type="text"
            name="sku"
            placeholder="Enter SKU"
            className="sep-input"
            value={sku}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="sep-toggle-row">
        <div className="sep-toggle-info">
          <div className="sep-toggle-title">Track stock</div>
          <div className="sep-toggle-desc">Update stock levels automatically when sold</div>
        </div>
        <button
          type="button"
          className={`sep-toggle ${trackStock === 'yes' ? 'sep-toggle--on' : ''}`}
          onClick={() => setTrackStock(trackStock === 'yes' ? 'no' : 'yes')}
        >
          <span className="sep-toggle-knob" />
        </button>
      </div>

      {/* Sell online — hidden until the booking-page sell-online flow ships. */}
      {false && (
        <div className="sep-toggle-row" style={{ borderBottom: 'none' }}>
          <div className="sep-toggle-info">
            <div className="sep-toggle-title">Sell online</div>
            <div className="sep-toggle-desc">Make this product available on your booking page</div>
          </div>
          <button
            type="button"
            className={`sep-toggle ${sellOnline === 'yes' ? 'sep-toggle--on' : ''}`}
            onClick={() => setSellOnline(sellOnline === 'yes' ? 'no' : 'yes')}
          >
            <span className="sep-toggle-knob" />
          </button>
        </div>
      )}
    </div>
  );

  return (
    <div className="h_n _new__service catalogue-no-header">
      <div className="body_container cm_bg_white">
        <div className="catalogue-page-wrapper">
          <CatalogueTopNav>
            <button type="button" className="catalogue-add-btn" onClick={handleSave}>
              Save
            </button>
          </CatalogueTopNav>
          <div className="catalogue-content-container">
            <div className="catalogue-content-wrapper">
              <div className="sep-tabs">
                {[
                  { id: 'details', label: 'Details' },
                  { id: 'images', label: 'Images' },
                  { id: 'pricing', label: 'Pricing' },
                  { id: 'stock', label: 'Stock' },
                ].map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`sep-tab ${activeTab === tab.id ? 'sep-tab--active' : ''}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div style={{ paddingTop: '24px' }}>
                {activeTab === 'details' && renderProductDetails()}
                {activeTab === 'images' && renderProductImages()}
                {activeTab === 'pricing' && renderPricing()}
                {activeTab === 'stock' && renderStock()}
              </div>
              <div style={{ height: '24px' }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function mapStateToProps(state) {
  return {
    ...state
  };
}

export default connect(mapStateToProps)(EditProduct);
